import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Mail, Phone, MapPin } from "lucide-react";
import { Helmet } from "react-helmet";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

/**
 * Privacy Policy — comprehensive DPDP Act 2023 (India) + GDPR-aware
 * disclosure document covering data collection, use, retention, cookies,
 * tracking technologies, user rights, security, third-party processors
 * and contact for privacy queries.
 *
 * Built to satisfy Google Ads / Search compliance bots that classify
 * sites with broad "wellness vocabulary" under stricter
 * transparency standards.
 */

const LAST_UPDATED = "5 February 2026";

export const PRIVACY_LAST_UPDATED = LAST_UPDATED;

export const SECTIONS = [
  {
    id: "introduction",
    title: "1. Introduction",
    body: (
      <>
        <p>
          Ezeefit Modular Furniture Private Limited (operating as "Ezeefit Office Systems", hereinafter "Ezeefit", "we", "us" or "our") is committed to protecting the privacy of users who access this website, request a quote, subscribe to the newsletter, or otherwise engage with our services. This Privacy Policy explains what personal information we collect, how we use it, with whom we share it, how long we retain it, and the rights you have under applicable law.
        </p>
        <p>
          This Policy is published pursuant to the requirements of the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act, India)</strong>, the <strong>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</strong>, and — where applicable to visitors from the European Economic Area — the <strong>General Data Protection Regulation (GDPR, EU 2016/679)</strong>.
        </p>
        <p>
          By using this website or submitting any information through any contact form, you confirm that you have read, understood and agreed to the terms of this Privacy Policy.
        </p>
      </>
    ),
  },
  {
    id: "data-fiduciary",
    title: "2. Data Fiduciary — Who We Are",
    body: (
      <>
        <p>For the purpose of the DPDP Act 2023, the Data Fiduciary is:</p>
        <ul>
          <li><strong>Legal entity:</strong> Ezeefit Modular Furniture Private Limited</li>
          <li><strong>Brand:</strong> Ezeefit Office Systems</li>
          <li><strong>GSTIN:</strong> 27AADCE6117G1ZT</li>
          <li><strong>Registered office:</strong> A-611-612, Crystal Plaza Premises, New Link Road, Andheri West, Mumbai 400053, Maharashtra, India</li>
          <li><strong>Manufacturing facility:</strong> Bhiwandi, Thane district, Maharashtra, India</li>
          <li><strong>Privacy contact (Grievance Officer):</strong> eos@ezeefitofficesystems.in</li>
        </ul>
      </>
    ),
  },
  {
    id: "information-we-collect",
    title: "3. Information We Collect",
    body: (
      <>
        <p>We collect personal information only where you knowingly provide it to us, or where it is automatically collected through standard web technologies. Specifically:</p>
        <h3>3.1 Information you provide directly</h3>
        <ul>
          <li><strong>Contact details</strong> — name, company name, designation, email address, phone number — submitted via our enquiry forms, quote requests, newsletter signup or project consultation requests.</li>
          <li><strong>Project information</strong> — office address, seat count, project scope, timeline, budget range and similar details you share while requesting a CAD layout, BOM or quote.</li>
          <li><strong>Account credentials</strong> — if you register for the Client Portal, your email address and a hashed password.</li>
          <li><strong>Correspondence</strong> — the content of emails, WhatsApp messages, contact form submissions and any other communication you send us.</li>
        </ul>
        <h3>3.2 Information collected automatically</h3>
        <ul>
          <li><strong>Device & browser information</strong> — IP address, browser type and version, operating system, device identifiers, screen resolution.</li>
          <li><strong>Usage data</strong> — pages visited, time spent on each page, click paths, referring URL, search queries used to reach our site.</li>
          <li><strong>Cookies & similar technologies</strong> — see Section 6 (Cookies and Tracking Technologies) below.</li>
        </ul>
        <h3>3.3 Information we do NOT collect</h3>
        <p>We do not knowingly collect:</p>
        <ul>
          <li>Sensitive personal data such as health records, biometric data, financial account numbers, racial/ethnic origin, religious beliefs, sexual orientation or political opinions.</li>
          <li>Information from children under the age of 18 (Section 10).</li>
          <li>Payment card information directly on this website (any payment, if applicable, is processed by authorised third-party payment processors).</li>
        </ul>
      </>
    ),
  },
  {
    id: "purposes",
    title: "4. How We Use Your Information",
    body: (
      <>
        <p>We use the information collected exclusively for the following legitimate business purposes:</p>
        <ul>
          <li><strong>Service delivery</strong> — responding to quote requests, preparing CAD layouts, BOMs and project proposals, coordinating site visits, scheduling manufacturing and installation.</li>
          <li><strong>Customer communication</strong> — sending project updates, invoices, delivery schedules, installation confirmations and warranty notifications.</li>
          <li><strong>Marketing communication</strong> — sending occasional newsletters, product launches and industry insights, only where you have explicitly opted in. You can unsubscribe at any time.</li>
          <li><strong>Website optimisation</strong> — analysing aggregate usage patterns to improve site performance, navigation and content relevance.</li>
          <li><strong>Legal & regulatory compliance</strong> — meeting our obligations under GST, Companies Act, tax authorities, and responding to lawful requests from regulatory bodies.</li>
          <li><strong>Fraud prevention & security</strong> — detecting and preventing unauthorised access, abuse of forms, and protecting against malicious activity.</li>
        </ul>
        <p>We do <strong>not</strong> use your personal information for automated decision-making or profiling that produces legal or significant effects on you.</p>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "5. Legal Basis for Processing",
    body: (
      <>
        <p>We process your personal information on one or more of the following legal bases:</p>
        <ul>
          <li><strong>Consent</strong> — where you have explicitly agreed (e.g., newsletter signup).</li>
          <li><strong>Contract performance</strong> — where processing is necessary to perform a contract with you (e.g., delivering a project you have commissioned).</li>
          <li><strong>Legitimate business interest</strong> — for purposes such as responding to enquiries you initiate, internal business operations and security.</li>
          <li><strong>Legal obligation</strong> — where required to comply with applicable Indian law, including the DPDP Act 2023 and tax statutes.</li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "6. Cookies and Tracking Technologies",
    body: (
      <>
        <p>Our website uses cookies and similar tracking technologies to operate the site and to understand how visitors use it. The categories are:</p>
        <h3>6.1 Strictly necessary cookies</h3>
        <p>Required for the website to function — including session management for the Client Portal, form security tokens and load balancing. These cannot be disabled.</p>
        <h3>6.2 Analytics cookies</h3>
        <p>We use <strong>Google Analytics 4</strong> to understand aggregate visitor behaviour — which pages are popular, how visitors arrive at the site, and where they spend time. Google Analytics anonymises IP addresses by default. You can opt out of Google Analytics tracking by installing the official Google Analytics Opt-out Browser Add-on at <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#F97316] underline">tools.google.com/dlpage/gaoptout</a>.</p>
        <h3>6.3 Advertising cookies</h3>
        <p>If you have visited our website from a Google Ads or other paid campaign, the corresponding ad-platform tag (such as Google Ads conversion tracking) may set cookies to measure campaign performance. These cookies are not used to build cross-site behavioural advertising profiles.</p>
        <h3>6.4 Managing cookies</h3>
        <p>You can control cookies through your browser settings — most browsers allow you to refuse, accept or delete cookies. Note that disabling strictly-necessary cookies may break parts of the website (such as the Client Portal login).</p>
      </>
    ),
  },
  {
    id: "sharing",
    title: "7. Sharing with Third Parties",
    body: (
      <>
        <p>We do not sell, rent or trade your personal information. We share it only with the following categories of recipients, strictly for the purposes described:</p>
        <ul>
          <li><strong>Service providers (Data Processors)</strong> — hosting providers, email delivery services (such as Resend), analytics providers (Google Analytics), CRM and project management tools — strictly to operate the website and process your enquiry. All processors are contractually bound to confidentiality and data-protection obligations.</li>
          <li><strong>Installation and logistics partners</strong> — where a site visit, delivery or installation requires it, we share the project address and on-site contact name/phone with the assigned project team.</li>
          <li><strong>Professional advisers</strong> — auditors, lawyers, accountants and bankers, where required for legitimate business operations.</li>
          <li><strong>Government and regulatory authorities</strong> — when compelled by lawful order, summons or statutory request (GST, income tax, courts, law enforcement).</li>
          <li><strong>Successor entities</strong> — in the event of a merger, acquisition, restructuring or sale of business assets, your information may transfer to the successor entity, subject to the same protections set out in this Policy.</li>
        </ul>
        <p>We do NOT share your personal information with advertising networks, data brokers, or any third party for their independent marketing purposes.</p>
      </>
    ),
  },
  {
    id: "retention",
    title: "8. How Long We Retain Information",
    body: (
      <>
        <ul>
          <li><strong>Quote enquiries that do not convert</strong> — retained for up to 24 months to allow re-engagement, then deleted.</li>
          <li><strong>Project records (executed projects)</strong> — retained for 7 years from project closure, in line with Indian Companies Act and Income Tax Act requirements.</li>
          <li><strong>Client Portal accounts</strong> — retained while the account is active; deleted within 12 months of account inactivity (you may request immediate deletion at any time).</li>
          <li><strong>Newsletter subscribers</strong> — retained until you unsubscribe.</li>
          <li><strong>Website analytics</strong> — aggregated, anonymised data retained indefinitely; visitor-level data deleted after 26 months (Google Analytics default).</li>
        </ul>
      </>
    ),
  },
  {
    id: "security",
    title: "9. Data Security",
    body: (
      <>
        <p>We implement reasonable and appropriate technical and organisational security measures consistent with the IT Rules 2011 and industry best practice, including:</p>
        <ul>
          <li>HTTPS / TLS encryption for all data transmitted between your browser and our servers.</li>
          <li>Password hashing using industry-standard one-way functions for any Client Portal credentials.</li>
          <li>Role-based access control — internal staff access to customer data is limited to personnel who require it for their role.</li>
          <li>Regular security audits, vulnerability assessments and dependency updates.</li>
          <li>Daily encrypted backups, with restricted access.</li>
        </ul>
        <p>Despite these measures, no internet transmission or electronic storage is 100% secure. We cannot guarantee absolute security of information transmitted to us. In the event of a personal data breach that is likely to result in a high risk to your rights and freedoms, we will notify you and the relevant Data Protection Board as required under the DPDP Act 2023.</p>
      </>
    ),
  },
  {
    id: "children",
    title: "10. Children's Data",
    body: (
      <>
        <p>Our services are intended for businesses and adult professionals. We do not knowingly collect personal information from children under the age of 18. If you believe a child has provided us with personal information, please contact eos@ezeefitofficesystems.in and we will promptly delete it.</p>
      </>
    ),
  },
  {
    id: "your-rights",
    title: "11. Your Rights",
    body: (
      <>
        <p>You have the following rights with respect to your personal information:</p>
        <ul>
          <li><strong>Right to access</strong> — request a copy of the personal information we hold about you.</li>
          <li><strong>Right to correction</strong> — request rectification of inaccurate or incomplete information.</li>
          <li><strong>Right to erasure</strong> — request deletion of your personal information, subject to legal retention obligations.</li>
          <li><strong>Right to withdraw consent</strong> — where processing is based on consent, you may withdraw it at any time without affecting the lawfulness of prior processing.</li>
          <li><strong>Right to grievance redressal</strong> — file a complaint with our Grievance Officer (details in Section 14).</li>
          <li><strong>Right to data portability</strong> (where applicable) — request a structured, commonly used and machine-readable copy of your data.</li>
          <li><strong>Right to object to direct marketing</strong> — you may unsubscribe from marketing communications at any time using the link in any marketing email, or by writing to eos@ezeefitofficesystems.in.</li>
        </ul>
        <p>To exercise any of these rights, please email <strong>eos@ezeefitofficesystems.in</strong> with your request. We will respond within <strong>30 days</strong>, except where the law allows a longer period. We may need to verify your identity before fulfilling certain requests.</p>
      </>
    ),
  },
  {
    id: "third-party-links",
    title: "12. Third-Party Links",
    body: (
      <>
        <p>Our website may contain links to third-party websites (such as social media platforms, partner directories, or news articles featuring our work). We are not responsible for the privacy practices or content of these third-party sites. We encourage you to read the privacy policy of any third-party site you visit.</p>
      </>
    ),
  },
  {
    id: "international-transfers",
    title: "13. International Data Transfers",
    body: (
      <>
        <p>Some of our service providers (such as Google Analytics, Resend, and hosting infrastructure) may store or process personal information in countries outside India, including in the United States and the European Economic Area. Where such transfers occur, we rely on:</p>
        <ul>
          <li>Standard Contractual Clauses or equivalent legal mechanisms approved by the relevant authority,</li>
          <li>Service providers certified under recognised data-protection frameworks where applicable, and</li>
          <li>Contractual safeguards requiring the recipient to maintain protection standards no less than those described in this Policy.</li>
        </ul>
      </>
    ),
  },
  {
    id: "grievance-officer",
    title: "14. Grievance Officer / Contact",
    body: (
      <>
        <p>If you have any questions, concerns, or grievances about this Privacy Policy or how we handle your personal information, please contact our Grievance Officer:</p>
        <div className="bg-white border border-[#E2E8F0] p-5 mt-3">
          <p className="text-sm leading-relaxed mb-1"><strong>Grievance Officer — Ezeefit Modular Furniture Private Limited</strong></p>
          <p className="text-sm text-[#475569] flex items-center gap-2 mb-1"><Mail className="w-3.5 h-3.5 text-[#F97316]" strokeWidth={2} /> eos@ezeefitofficesystems.in</p>
              <p className="text-sm text-[#475569] flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#F97316]" strokeWidth={2} /> <a href="tel:+919820596811" className="phone-flash">9820596811</a> <span className="text-[#94A3B8]">/</span> <a href="tel:+919820042100" className="hover:text-[#F97316]">9820042100</a></p>
        </div>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "15. Intellectual Property Rights",
    body: (
      <>
        <p>Ezeefit Modular Furniture Private Limited is the owner of copyright in the entire content on this website. No material from this site may be copied, modified, reproduced, republished, uploaded, transmitted, posted or distributed in any form without prior written permission from Ezeefit. Any use of the content of this website for a purpose which is not consistent with fair use provisions of copyright law is prohibited and may amount to infringement of copyright.</p>
        <p>Trademarks, logos, and labels displayed on this website are trademarks or registered trademarks of Ezeefit or its group or its affiliates unless otherwise specified. These trademarks may not be used in any manner without prior written consent from Ezeefit. Any unauthorised use may amount to infringement and violation of trademark and other applicable laws and could result in criminal and/or civil penalties.</p>
      </>
    ),
  },
  {
    id: "updates",
    title: "16. Updates to This Privacy Policy",
    body: (
      <>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements or other factors. When we make a material change, we will revise the "Last updated" date at the top of this Policy and, where appropriate, notify you by email or via a prominent notice on the website.</p>
        <p>We encourage you to review this Policy periodically. Continued use of the website after a revised Policy is published constitutes acceptance of the revised terms.</p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "17. Governing Law and Jurisdiction",
    body: (
      <>
        <p>This Privacy Policy is governed by the laws of the Republic of India. Any disputes arising out of or in connection with this Policy shall be subject to the exclusive jurisdiction of the courts at Mumbai, Maharashtra.</p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  useCanonical("https://ezeefitofficesystems.in/privacy-policy");
  useMetaDescription("Privacy Policy — Ezeefit Office Systems. Data collection, cookies, third-party sharing & user rights under India's DPDP Act 2023 and GDPR.");

  useEffect(() => {
    document.title = "Privacy Policy | DPDP Act 2023 Compliant | Ezeefit Office Systems";
  }, []);

  return (
    <div className="pt-20" data-testid="privacy-policy-page">
      <Helmet>
        <meta name="description" content="Privacy Policy — Ezeefit Office Systems. Data collection, cookies, third-party sharing & user rights under India's DPDP Act 2023 and GDPR." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#0F172A] noise-overlay">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors text-sm" data-testid="privacy-back-home">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#F97316]/40 mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-[#F97316]" strokeWidth={2} />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#F97316]">Your Privacy Matters</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="privacy-h1">Privacy Policy</h1>
            <p className="text-[#94A3B8] text-sm mt-4">Last updated: {LAST_UPDATED}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">

          {/* Table of contents */}
          <div className="bg-white border border-[#E2E8F0] p-6 mb-10" data-testid="privacy-toc">
            <h2 className="text-sm font-mono uppercase tracking-widest text-[#F97316] mb-4">Table of contents</h2>
            <ol className="space-y-1 text-sm text-[#475569]">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="hover:text-[#F97316] transition-colors">{s.title}</a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          {SECTIONS.map((s, i) => (
            <motion.div
              key={s.id}
              id={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.02, 0.2) }}
              className="mb-12 scroll-mt-24"
              data-testid={`privacy-section-${s.id}`}
            >
              <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-5 tracking-tight">{s.title}</h2>
              <div className="prose prose-sm md:prose-base max-w-none text-[#475569] [&_p]:leading-relaxed [&_p]:mb-4 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-[#0F172A] [&_h3]:mt-5 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ul]:mb-4 [&_li]:text-sm md:[&_li]:text-base">
                {s.body}
              </div>
            </motion.div>
          ))}

          {/* Closing footer note */}
          <div className="mt-16 pt-8 border-t border-[#E2E8F0] text-center">
            <p className="text-xs text-[#94A3B8]">
              © {new Date().getFullYear()} Ezeefit Modular Furniture Private Limited. All rights reserved. GSTIN: 27AADCE6117G1ZT.
            </p>
            <p className="text-xs text-[#94A3B8] mt-2">
              For privacy queries write to <a href="mailto:eos@ezeefitofficesystems.in" className="text-[#F97316] underline" data-testid="privacy-contact-email">eos@ezeefitofficesystems.in</a>
            </p>
          </div>

        </div>
      </section>
    </div>
  );
}
